import styled from 'styled-components';
import { Typography } from '@/components';
import { TextStyles, theme } from '@/constants';
import type { IProgressStage, TrackingMilestone } from '@/modules/pmt/interfaces';

export const TrackerWrapper = styled.div<{
  milestone: TrackingMilestone;
  hasIssue: boolean;
  stageList: Array<IProgressStage>;
}>`
  ${({ milestone, hasIssue, stageList }) => getProgressStyles(milestone, hasIssue, stageList)}
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[22]};
`;

export const ProgressTitle = styled(Typography).attrs({
  variant: TextStyles['Overline'],
})`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: max-content;
  text-transform: uppercase;
  margin-top: ${({ theme }) => theme.spacing[4]};
  color: ${({ theme }) => theme.colors.highEmphasisBlack};
`;

export const ProgressItem = styled.div`
  position: relative;
  z-index: 1;

  &:nth-child(3) {
    ${ProgressTitle} {
      left: 0;
      transform: unset;
    }
  }

  &:last-child {
    ${ProgressTitle} {
      left: 100%;
      transform: translateX(-100%);
    }
  }
`;

export const SmallCircle = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.lightSilver};
`;

export const BigCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.pastelBlue};
`;

export const TrackerDashedLine = styled.div`
  position: absolute;
  width: 100%;
  border-top: 2px dashed ${({ theme }) => theme.colors.pressedBlack};
`;

export const TrackerFullLine = styled.div`
  position: absolute;
  width: 0;
  border-top: 2px solid transparent;
`;

const getProgressStyles = (milestone: TrackingMilestone, hasIssue = false, stageList: Array<IProgressStage>) => {
  let matchedIndex = -1;
  const matchedProgress = stageList?.find((progress, index) => {
    if (progress.milestones.has(milestone)) {
      matchedIndex = index;
      return true;
    }
    return false;
  });

  if (!matchedProgress) return;

  const stageListLength = stageList.length;
  const ERROR_COLOR = theme.colors.darkError;

  const trackerWidthCounter =
    matchedIndex === 0 ? 0 : matchedIndex === stageListLength - 1 ? 100 : (100 / (stageListLength - 1)) * matchedIndex;
  let currentStyle = {
    //default
    phaseColor: theme.colors.pastelBlue,
    trackerWidth: trackerWidthCounter + '%',
    nthChild: '-n+' + (matchedIndex + 2),
  };

  const { colorScheme } = matchedProgress;
  if (colorScheme) {
    currentStyle = {
      ...currentStyle,
      phaseColor: colorScheme,
    };
  }

  const phaseColor = hasIssue ? ERROR_COLOR : currentStyle.phaseColor;
  return `
    ${BigCircle} {
      background: ${phaseColor};
    }
    ${TrackerFullLine} {
      width: ${currentStyle.trackerWidth};
      border-color: ${phaseColor};
    }
    ${ProgressItem}:nth-child(${currentStyle.nthChild}) {
      ${SmallCircle} {
        background: ${phaseColor};
      }
    }
  `;
};
